<template>
  <div v-if="ticket.sections?.cloudThreatMetaData?.length">
    <div>
      <v-expansion-panels
        v-model="eventsExpanded"
        flat
        multiple
        class="access-permission-policy-section"
        :ripple="false"
      >
        <v-expansion-panel
          :hide-actions="true"
          v-for="(event, i) in ticket.sections?.cloudThreatMetaData"
          :key="i"
          data-testid="scheduled-simulations-expansion-panel"
        >
          <template #title>
            <div class="d-flex w-100">
              <div class="d-flex pr-4 expander-title-width bg-indigo-faint w-100">
                <v-icon
                  :class="{
                    rotated:
                      ticket.sections?.cloudThreatMetaData?.length && eventsExpanded.includes(i),
                  }"
                  icon="$chevron"
                  class="ml-4 mt-2 text-primary"
                  size="36"
                ></v-icon>
                <div class="mb-6 mt-4 ml-2">
                  <div class="subtitle1 mb-2">
                    {{
                      $t(
                        "ticketDetails.fullDetails.impossibleTraveler.eventN",
                        ticket.sections?.cloudThreatMetaData?.length - i
                      )
                    }}
                  </div>
                  <div>
                    <span class="subtitle2">
                      {{
                        $t("ticketDetails.fullDetails.impossibleTraveler.from", {
                          location: formatEventLocation(event.previousLogin),
                        })
                      }}</span
                    >
                    ({{ getFormattedDateTime(event.previousLogin.timestamp) }})
                  </div>
                  <div>
                    <span class="subtitle2">
                      {{
                        $t("ticketDetails.fullDetails.impossibleTraveler.to", {
                          location: formatEventLocation(event.currentLogin),
                        })
                      }}</span
                    >
                    ({{ getFormattedDateTime(event.currentLogin.timestamp) }})
                  </div>
                  <div>
                    <span class="subtitle2">
                      {{
                        $t("ticketDetails.fullDetails.impossibleTraveler.timeBetweenLogins")
                      }}</span
                    >
                    {{
                      getTimeBetweenLogins(
                        event.previousLogin.timestamp,
                        event.currentLogin.timestamp
                      )
                    }}
                  </div>
                  <div v-if="event.distance">
                    <span class="subtitle2">
                      {{
                        $t("ticketDetails.fullDetails.impossibleTraveler.distanceBetweenLocations")
                      }}</span
                    >
                    {{
                      $t("ticketDetails.fullDetails.impossibleTraveler.distance", {
                        distance: formatDistance(event.distance),
                      })
                    }}
                  </div>
                </div>
              </div>
            </div>
          </template>
          <v-expansion-panel-text>
            <v-table>
              <thead>
                <tr>
                  <th class="w-5" :aria-label="$t('ariaLabels.indicatorColumn')"></th>
                  <th>{{ $t("ticketDetails.fullDetails.impossibleTraveler.type") }}</th>
                  <th>{{ $t("ticketDetails.fullDetails.impossibleTraveler.time") }}</th>
                  <th>{{ $t("ticketDetails.fullDetails.impossibleTraveler.countryIp") }}</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td class="w-5"></td>
                  <td class="subtitle2">
                    <div class="inner-circle mr-4"></div>
                    {{ $t("ticketDetails.fullDetails.impossibleTraveler.sourceLogin") }}
                  </td>
                  <td class="body2">
                    <div>
                      {{ getFormattedDateTime(event.previousLogin.timestamp, "ddd, MMM DD, YYYY") }}
                    </div>
                    <div>
                      {{ getFormattedDateTime(event.previousLogin.timestamp, "h:mm A") }}
                    </div>
                  </td>
                  <td class="body2">
                    <div>{{ formatEventLocation(event.previousLogin) }}</div>
                    <div>{{ event.previousLogin.ip }}</div>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td class="pt-4 pb-4" colspan="3">
                    <span class="subtitle2 mr-2">{{
                      $t("ticketDetails.fullDetails.impossibleTraveler.isp")
                    }}</span>
                    <span class="body2">{{ event.previousLogin.isp }}</span>
                    <div class="mt-4">
                      <span class="subtitle2 mr-2">{{
                        $t("ticketDetails.fullDetails.impossibleTraveler.service")
                      }}</span>
                      <span class="body2">{{ $t(`services.${ticket.sections.serviceName}`) }}</span>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td></td>
                  <td class="subtitle2">
                    <div class="inner-circle mr-4"></div>
                    {{ $t("ticketDetails.fullDetails.impossibleTraveler.destinationLogin") }}
                  </td>
                  <td class="body2">
                    <div>
                      {{ getFormattedDateTime(event.currentLogin.timestamp, "ddd, MMM DD, YYYY") }}
                    </div>
                    <div>
                      {{ getFormattedDateTime(event.currentLogin.timestamp, "h:mm A") }}
                    </div>
                  </td>
                  <td class="body2">
                    <div>{{ formatEventLocation(event.currentLogin) }}</div>
                    <div>{{ event.currentLogin.ip }}</div>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td class="pt-4 pb-4" colspan="3">
                    <span class="subtitle2 mr-2">{{
                      $t("ticketDetails.fullDetails.impossibleTraveler.isp")
                    }}</span>
                    <span class="body2">{{ event.currentLogin.isp }}</span>
                    <div class="mt-4">
                      <span class="subtitle2 mr-2">{{
                        $t("ticketDetails.fullDetails.impossibleTraveler.service")
                      }}</span>
                      <span class="body2">{{ $t(`services.${ticket.sections.serviceName}`) }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script lang="ts">
import { getFormattedDateTime } from "@/_helpers/date";
import { defineComponent, type PropType, ref } from "vue";
import type { ImpossibleTravelerLogin, TicketDetails } from "@/_store/tickets/tickets.module";
import { useI18n } from "vue-i18n";
import moment from "moment";

export default defineComponent({
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup() {
    const i18n = useI18n();
    const eventsExpanded = ref<number[]>([]);

    const formatEventLocation = (loginData: ImpossibleTravelerLogin) => {
      return [loginData.cityName, loginData.stateName, loginData.countryName]
        .filter((item) => !!item)
        .join(", ");
    };

    const distanceFormatter = new Intl.NumberFormat("en-US");

    const formatDistance = (distance: number) => {
      const normalizedDistance = Math.floor(distance);
      return normalizedDistance >= 10000
        ? distanceFormatter.format(normalizedDistance)
        : String(normalizedDistance);
    };

    const getTimeBetweenLogins = (previousLogin: number, currentLogin: number) => {
      if (currentLogin - previousLogin < 1000) {
        return i18n.t("general.minutes", 0);
      }
      const diff = moment.duration(moment(currentLogin).diff(moment(previousLogin)));

      const hours = Math.floor(diff.asHours());
      const minutes = diff.minutes();

      let resultString = "";

      if (hours) {
        resultString += i18n.t("general.hours", hours) + " ";
      }

      if (minutes) {
        resultString += i18n.t("general.minutes", minutes);
      }

      return resultString;
    };

    return {
      getFormattedDateTime,
      eventsExpanded,
      formatEventLocation,
      formatDistance,
      getTimeBetweenLogins,
    };
  },
});
</script>

<style lang="scss" scoped>
.full-details-wrapper {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
}

.ip-col {
  max-width: 40%;
  word-break: break-word;
}

:deep(*) {
  .v-expansion-panel-title {
    padding-bottom: 0;
  }

  .v-expansion-panel--active + .v-expansion-panel {
    margin-top: 0;
  }

  .v-expansion-panel-text__wrapper {
    padding: 0;
    border: 1px solid rgb(var(--v-theme-indigo-pale));
    margin: 0 24px;
  }
}

.inner-circle {
  background-color: rgb(var(--v-theme-red-dark));
  border-color: rgb(var(--v-theme-red-dark));
}
</style>
